import { fetchMetadataLocalStorage, setMetadataLocalStorage } from '@hcengineering/ui'
import { deepEqual } from 'fast-equals'
import { type Unsubscriber, writable } from 'svelte/store'
import presentation from './plugin'

migrateDrafts()
export const draftsStore = writable<Record<string, any>>(fetchMetadataLocalStorage(presentation.metadata.Draft) ?? {})
let drafts: Record<string, any> = fetchMetadataLocalStorage(presentation.metadata.Draft) ?? {}
const activeDraftsKey = 'activeDrafts'
export const activeDraftsStore = writable<Set<string>>(new Set())
const activeDrafts = new Set<string>()
const localActiveDrafts = new Set<string>()

export interface DraftEntry<T = any> {
  id: string
  value: T
  active: boolean
}

export type DraftMatcher<T = any> = (id: string, value: any) => value is T

window.addEventListener('storage', storageHandler)

function storageHandler (evt: StorageEvent): void {
  if (evt.storageArea === localStorage) {
    if (evt.key !== presentation.metadata.Draft) return
    if (evt.newValue !== null) {
      drafts = JSON.parse(evt.newValue)
      draftsStore.set(drafts)
    }
  }
}

function syncDrafts (): void {
  draftsStore.set(drafts)
  setMetadataLocalStorage(presentation.metadata.Draft, drafts)
}

// #region Broadcast

const bc = 'BroadcastChannel' in window ? new BroadcastChannel(activeDraftsKey) : undefined

type BroadcastMessage = BroadcastGetMessage | BroadcastGetResp | BroadcastAddMessage | BroadcastRemoveMessage

interface BroadcastGetMessage {
  type: 'get_all'
}

interface BroadcastGetResp {
  type: 'get_all_response'
  value: string[]
}

interface BroadcastRemoveMessage {
  type: 'remove'
  value: string
}

interface BroadcastAddMessage {
  type: 'add'
  value: string
}

function sendMessage (req: BroadcastMessage): void {
  bc?.postMessage(req)
}

function syncActive (): void {
  activeDraftsStore.set(activeDrafts)
}

function loadActiveDrafts (): void {
  activeDrafts.clear()
  syncActive()
  sendMessage({ type: 'get_all' })
}

if (bc !== undefined) {
  bc.onmessage = (e: MessageEvent<BroadcastMessage>) => {
    if (e.data.type === 'get_all') {
      sendMessage({ type: 'get_all_response', value: Array.from(localActiveDrafts.values()) })
    }
    if (e.data.type === 'get_all_response') {
      for (const val of e.data.value) {
        activeDrafts.add(val)
      }
      syncActive()
    }
    if (e.data.type === 'add') {
      activeDrafts.add(e.data.value)
      syncActive()
    }
    if (e.data.type === 'remove') {
      if (!localActiveDrafts.has(e.data.value)) {
        activeDrafts.delete(e.data.value)
      }
      syncActive()
    }
  }
}

loadActiveDrafts()

// #endregion

// #region Active

function addActive (id: string): void {
  if (!localActiveDrafts.has(id)) {
    localActiveDrafts.add(id)
    activeDrafts.add(id)
    syncActive()
    sendMessage({ type: 'add', value: id })
  }
}

function deleteActive (id: string): void {
  if (localActiveDrafts.has(id)) {
    localActiveDrafts.delete(id)
    activeDrafts.delete(id)
    syncActive()
    sendMessage({ type: 'remove', value: id })
  }
}

// #endregion

function isEmptyDraft<T> (object: T, emptyObj: Partial<T> | undefined): boolean {
  for (const key in object) {
    if (key === '_id') continue
    const value = object[key]
    let res: boolean = false
    if (Array.isArray(value)) {
      res = value.length > 0
      if (res && emptyObj != null) {
        res = !deepEqual(value, emptyObj[key])
      }
    } else {
      res = value != null
      if (res && typeof value === 'string') {
        res = value.trim() !== ''
      }
      if (res && typeof value === 'number') {
        res = value !== 0
      }
      if (res && emptyObj != null) {
        res = !deepEqual(value, emptyObj[key])
      }
    }
    if (res) {
      return false
    }
  }
  return true
}

function removeDraft (id: string, parentId: string | undefined = undefined): void {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete drafts[id]
  deleteActive(id)
  syncDrafts()
  if (parentId !== undefined) {
    MultipleDraftController.remove(parentId, id)
  }
}

export class DraftController<T> {
  private unsub: Unsubscriber | undefined = undefined
  constructor (
    private readonly id: string | undefined,
    private readonly parentId: string | undefined = undefined
  ) {
    if (this.id !== undefined) {
      addActive(this.id)
    }
  }

  static remove (id: string): void {
    removeDraft(id)
  }

  static save<T>(id: string, object: T, emptyObj: Partial<T> | undefined = undefined): void {
    if (emptyObj !== undefined && isEmptyDraft(object, emptyObj)) {
      DraftController.remove(id)
      return
    }
    drafts[id] = object
    addActive(id)
    syncDrafts()
  }

  destroy (): void {
    this.unsub?.()
    if (this.id !== undefined) {
      deleteActive(this.id)
    }
  }

  subscribe (callback: (val: T | undefined) => void): Unsubscriber {
    callback(this.get())
    this.unsub = draftsStore.subscribe((p) => {
      callback(this.getValue(p))
    })
    return () => {
      this.destroy()
    }
  }

  private getValue (store: Record<string, any>): T | undefined {
    if (this.id !== undefined) {
      const res = store[this.id]
      return res
    }
  }

  get (): T | undefined {
    return this.getValue(drafts)
  }

  save (object: T, emptyObj: Partial<T> | undefined = undefined): void {
    if (emptyObj !== undefined && isEmptyDraft(object, emptyObj)) {
      this.remove()
      return
    }
    if (this.id !== undefined) {
      drafts[this.id] = object
      syncDrafts()
      addActive(this.id)
      if (this.parentId !== undefined) {
        MultipleDraftController.add(this.parentId, this.id)
      }
    }
  }

  remove (): void {
    if (this.id !== undefined) {
      removeDraft(this.id, this.parentId)
    }
  }
}

export class MultipleDraftController {
  constructor (
    private readonly id: string,
    private readonly matcher?: DraftMatcher
  ) {}

  static remove (id: string, value: string): void {
    const arr: string[] = drafts[id] ?? []
    const index = arr.findIndex((p) => p === value)
    if (index !== -1) {
      arr.splice(index, 1)
      drafts[id] = arr
      syncDrafts()
    }
  }

  static add (id: string, value: string): void {
    const arr: string[] = drafts[id] ?? []
    if (!arr.includes(value)) {
      arr.push(value)
    }
    drafts[id] = arr
    syncDrafts()
  }

  private getIds (store: Record<string, any>): string[] {
    const value = store[this.id] ?? []
    const ids = Array.isArray(value) ? value.filter((it): it is string => typeof it === 'string') : []

    if (this.matcher !== undefined) {
      for (const [draftId, draft] of Object.entries(store)) {
        if (draftId === this.id || ids.includes(draftId)) continue
        if (this.matcher(draftId, draft)) {
          ids.push(draftId)
        }
      }
    }

    return ids
  }

  private getAllFromStore<T = any> (store: Record<string, any>, active: Set<string>): Array<DraftEntry<T>> {
    const result: Array<DraftEntry<T>> = []

    for (const id of this.getIds(store)) {
      const value = store[id]
      if (value === undefined) continue
      if (this.matcher !== undefined && !this.matcher(id, value)) continue
      result.push({
        id,
        value,
        active: active.has(id)
      })
    }

    return result
  }

  getAll<T = any> (): Array<DraftEntry<T>> {
    return this.getAllFromStore<T>(drafts, activeDrafts)
  }

  getNext (): string | undefined {
    for (const val of this.getIds(drafts)) {
      if (!activeDrafts.has(val)) {
        return val
      }
    }
  }

  hasNext (callback: (value: boolean) => void): Unsubscriber {
    const next = this.getNext()
    // eslint-disable-next-line
    callback(next !== undefined)
    const draftSub = draftsStore.subscribe((drafts) => {
      for (const val of this.getIds(drafts)) {
        if (!activeDrafts.has(val)) {
          // eslint-disable-next-line
          callback(true)
          return
        }
      }
      // eslint-disable-next-line
      callback(false)
    })
    const activeSub = activeDraftsStore.subscribe((activeDrafts) => {
      for (const val of this.getIds(drafts)) {
        if (!activeDrafts.has(val)) {
          // eslint-disable-next-line
          callback(true)
          return
        }
      }
      // eslint-disable-next-line
      callback(false)
    })
    return () => {
      draftSub()
      activeSub()
    }
  }

  hasAny (callback: (value: boolean) => void): Unsubscriber {
    callback(this.getAll().length > 0)
    return draftsStore.subscribe((drafts) => {
      callback(this.getAllFromStore(drafts, activeDrafts).length > 0)
    })
  }
}

function migrateDrafts (): void {
  const drafts = fetchMetadataLocalStorage(presentation.metadata.Draft) ?? {}
  const issues = drafts['tracker:ids:IssueDraft']
  if (!Array.isArray(issues)) {
    drafts['tracker:ids:IssueDraft'] = []
  }
  const candidates = drafts['recruit:mixin:Candidate']
  if (!Array.isArray(candidates)) {
    drafts['recruit:mixin:Candidate'] = []
  }
  setMetadataLocalStorage(presentation.metadata.Draft, drafts)
}
